<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800">Gráfica de Ingresos por Método de Pago</h1>
      <div class="flex space-x-2 items-center">
        <input type="date" v-model="fechas.fecha_inicio" class="px-4 py-2 border rounded">
        <input type="date" v-model="fechas.fecha_fin" class="px-4 py-2 border rounded">
        <button @click="cargarDatos" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          Consultar
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-10">
      <p>Cargando datos...</p>
    </div>
    <div v-if="error" class="text-center py-10 text-red-500">
      <p>{{ error }}</p>
    </div>

    <div v-if="!loading && !error && datos" class="bg-white rounded-lg shadow p-6 mb-6">
      <div class="mb-6">
        <h2 class="text-xl font-semibold mb-2">Distribución de Ingresos</h2>
        <p class="text-gray-600">
          Visualización de los ingresos generados por cada método de pago durante el período seleccionado.
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div class="lg:col-span-2">
          <div class="border rounded-lg p-4 h-96 flex items-center justify-center">
            <apexchart v-if="series.length > 0" type="donut" :options="chartOptions" :series="series"></apexchart>
          </div>
        </div>
        
        <div class="lg:col-span-1">
          <div class="border rounded-lg p-4 h-96 overflow-y-auto">
            <h3 class="font-semibold mb-4">Resumen por Método de Pago</h3>
            <div v-if="datos.ingresos_por_metodo.length > 0" class="space-y-4">
              <div 
                v-for="(metodo, index) in datos.ingresos_por_metodo" 
                :key="index"
                class="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                :style="`border-left: 4px solid ${colores[index % colores.length]}`"
              >
                <div class="flex items-center">
                  <div class="w-3 h-3 rounded-full mr-2" :style="`background-color: ${colores[index % colores.length]}`"></div>
                  <span>{{ metodo.metodo_pago }}</span>
                </div>
                <div class="text-right">
                  <div class="font-medium">{{ formatCurrency(metodo.total) }}</div>
                  <div class="text-sm text-gray-500">{{ calcularPorcentaje(metodo.total) }}% del total</div>
                </div>
              </div>
            </div>
             <div v-else class="text-center text-gray-500 py-4">No hay datos de ingresos.</div>
            
            <div class="mt-6 pt-4 border-t">
              <div class="flex justify-between items-center">
                <span class="font-semibold">Total de ingresos:</span>
                <span class="text-xl font-bold">{{ formatCurrency(totalIngresos) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-8">
        <h3 class="text-lg font-semibold mb-4">Detalle de Transacciones</h3>
        <div v-if="datos.transacciones.length > 0" class="overflow-x-auto">
          <table class="min-w-full bg-white">
            <thead>
              <tr class="bg-gray-100">
                <th class="py-3 px-4 text-left">ID Transacción</th>
                <th class="py-3 px-4 text-left">Fecha</th>
                <th class="py-3 px-4 text-left">Cliente</th>
                <th class="py-3 px-4 text-left">Método de Pago</th>
                <th class="py-3 px-4 text-right">Monto</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="transaccion in datos.transacciones" :key="transaccion.id" class="border-t hover:bg-gray-50">
                <td class="py-3 px-4">#{{ transaccion.id }}</td>
                <td class="py-3 px-4">{{ formatDate(transaccion.fecha) }}</td>
                <td class="py-3 px-4">{{ transaccion.cliente }}</td>
                <td class="py-3 px-4">
                  <span class="px-2 py-1 text-xs rounded-full" :style="`background-color: ${getColorMetodo(transaccion.metodo_pago)}20; color: ${getColorMetodo(transaccion.metodo_pago)};`">
                    {{ transaccion.metodo_pago }}
                  </span>
                </td>
                <td class="py-3 px-4 text-right">{{ formatCurrency(transaccion.monto) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center text-gray-500 py-8">No hay transacciones en el período seleccionado.</div>
      </div>
    </div>
  </div>
</template>

<script>
import reportesNuevos from '@/Services/reportesNuevos.js';
import VueApexCharts from 'vue3-apexcharts';

export default {
  name: 'IngresosMetodosPago',
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);

    return {
      fechas: {
        fecha_inicio: startDate.toISOString().split('T')[0],
        fecha_fin: endDate.toISOString().split('T')[0],
      },
      datos: null,
      loading: false,
      error: null,
      colores: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1'],
      series: [],
      chartOptions: {
        chart: {
          type: 'donut',
        },
        labels: [],
        colors: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1'],
        legend: {
          position: 'right'
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }],
        tooltip: {
          y: {
            formatter: (val) => {
              return val.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
            }
          }
        }
      },
    };
  },
  computed: {
    totalIngresos() {
      if (!this.datos || !this.datos.ingresos_por_metodo) return 0;
      return this.datos.ingresos_por_metodo.reduce((sum, metodo) => sum + parseFloat(metodo.total), 0);
    },
  },
  methods: {
    async cargarDatos() {
      this.loading = true;
      this.error = null;
      this.datos = null;
      try {
        const response = await reportesNuevos.GRAFICA_INGRESOS_METODO_PAGO(this.fechas);
        if (response.data.errors) {
          this.error = Object.values(response.data.errors).join(', ');
          return;
        }
        this.datos = response.data;
        this.datos = response.data;
        this.actualizarGrafica();
      } catch (err) {
        console.error('Error al cargar datos:', err);
        this.error = 'No se pudieron cargar los datos. Intente de nuevo más tarde.';
      } finally {
        this.loading = false;
      }
    },
    getColorMetodo(metodo) {
      if (!this.datos || !this.datos.ingresos_por_metodo) return '#9CA3AF';
      const index = this.datos.ingresos_por_metodo.findIndex(m => m.metodo_pago === metodo);
      return index !== -1 ? this.colores[index % this.colores.length] : '#9CA3AF';
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('es-MX', options);
    },
    formatCurrency(value) {
        if (typeof value !== 'number') {
            value = parseFloat(value) || 0;
        }
        return value.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    },
    calcularPorcentaje(monto) {
      if (this.totalIngresos === 0) return 0;
      return ((parseFloat(monto) / this.totalIngresos) * 100).toFixed(1);
    },
    actualizarGrafica() {
      if (!this.datos || !this.datos.ingresos_por_metodo) return;

      const labels = this.datos.ingresos_por_metodo.map(m => m.metodo_pago);
      const series = this.datos.ingresos_por_metodo.map(m => parseFloat(m.total));

      this.series = series;
      this.chartOptions = {
        ...this.chartOptions,
        labels: labels
      };
    },
  },
  mounted() {
    this.cargarDatos();
  }
};
</script>

<style scoped>
/* Estilos específicos para este componente */
</style>
